import { useState, useEffect, useContext, useRef } from 'react';

import { LiveReserveContext } from './LiveLessonReseveContext';
import LiveReserveMaintenance from './maintenance/LiveReserveMaintenance';
import LiveReserveForm from './form/LiveReserveForm';
import LiveReserveInstructorDetail from './form/selectInstructor/detail/LiveReserveInstructorDetail';
import Loading from '../../../common/loading/Loading';

import { requestApi } from '../../../../util/utility';

import { LiveReserveSituationDisplaySituations } from '../../../../models/lesson/live/reserve/situation/LiveReserveSituation';
import { LiveReserveInstructorModel } from '../../../../models/lesson/live/reserve/instructor/LiveReserveInstructor';
import {
  LiveReserveRequestPrefs,
  emptyLiveReserveRequestPrefs,
} from '../../../../models/lesson/live/reserve/request/LiveReserveRequest';


function LiveReserve() {
  // パラメータ(初回データ)
  const params = new URLSearchParams(window.location.search);
  const dateNow = params.get('date_now') || '';
  const dfInstId = params.get('inst_id') || '';
  // 不正な値が指定された場合はデフォルト値として 15 を設定
  const lessonTermMinute = 15;
  let dfLessonMinutes: number = parseInt(params.get('lesson_minutes') || String(lessonTermMinute), 10);
  if (![lessonTermMinute, lessonTermMinute*2, lessonTermMinute*3].includes(dfLessonMinutes)) {
    dfLessonMinutes = lessonTermMinute;
  }
  const [user, setUser] = useState<any>(null);
  const [config, setConfig] = useState(null);
  const [displaySituations, setLiveReserveSituationDisplaySituations] = useState<LiveReserveSituationDisplaySituations>([]);
  const [vars, setVars] = useState(null);
  const formRef = useRef<HTMLFormElement>(null);
  // 入力値
  const [instId, setInstId] = useState<string|null>(dfInstId);
  const [lsnMin, setLsnMin] = useState<number>(dfLessonMinutes);
  const [isTrial, setIsTrial] = useState<boolean>(true);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string|null>(null);
  const [selectedSituation, setSelectedSituation] = useState<string|null>(null);
  const [requestContent, setRequestContent] = useState<string>('');
  // SPEAKOUT-1853 講師リクエスト（希望日時 第1〜第3）。
  //   予約は枠のID(selectedScheduleId)を送るが、リクエストは枠が無いところへ希望を出すので
  //   対応するIDが無い。別の state で持ち、送信先も /inquiry に分かれる。
  const [requestPrefs, setRequestPrefs] = useState<LiveReserveRequestPrefs>(emptyLiveReserveRequestPrefs());
  // 空き枠はあるが「他の日時をリクエストする」を生徒が選んだか
  const [isRequestSelected, setIsRequestSelected] = useState<boolean>(false);
  // SPEAKOUT-1853 詳細（プロフィール／レビュー）を開いている講師。null なら閉じている
  const [detailInstId, setDetailInstId] = useState<string|null>(null);
  // 出力値
  const [title, setTitle] = useState<string>('');
  const [coachingName, setCoachingName] = useState<string>('');
  const [isLiveMaintenance, setIsLiveMaintenance] = useState<boolean>(false);
  const [instructors, setInstructors] = useState<LiveReserveInstructorModel[]>([]);
  // SPEAKOUT-1853 講師リクエスト用。予約枠が1つも無くて上の一覧から外れた講師と、
  //   受付できる最も早い日付（サーバーが算出したもの＝画面とサーバーで判定をそろえる）
  const [noSlotInstructors, setNoSlotInstructors] = useState<LiveReserveInstructorModel[]>([]);
  const [requestMinDate, setRequestMinDate] = useState<string>('');
  const [requestMaxDate, setRequestMaxDate] = useState<string>('');
  const [isCoaching, setIsCoaching] = useState<boolean>(false);
  const [isLesson, setIsLesson] = useState<boolean>(false);
  const [calendar, setCalendar] = useState([]);
  const [chkCal, setChkCal] = useState({});
  const [trialCount, setTrialCount] = useState<number | null>(null);
  const [isTicketLess, setIsTicketLess] = useState<boolean>(false);
  const [lessTicketCount, setLessTicketCount] = useState<number>(0);
  // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
  const [isLsnSvyAddTicketTerm, setIsLsnSvyAddTicketTerm] = useState<boolean>(false);
  const [lsnSvyAddTicketStart, setLsnSvyAddTicketStart] = useState<Date | null>(null);
  const [lsnSvyAddTicketEnd, setLsnSvyAddTicketEnd] = useState<Date | null>(null);

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  // サーバアクセス
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        // API実行
        const resp = await requestApi({
          inc: '/backend/api/functions/user/lesson/live',
          date_now: dateNow,
          inst_id: instId,
          lesson_minutes: lsnMin,
        });
        if (cancelled) return;

        // 取得データの格納
        setLiveReserveSituationDisplaySituations(resp.displaySituations);
        setConfig(resp.config);
        setTitle(resp.title);
        setCoachingName(resp.COACHING_NAME);
        setVars(resp.vars);
        setUser(resp.user);
        setIsLiveMaintenance(resp.isLiveMaintenance);
        setInstructors(resp.instructor);
        setNoSlotInstructors(resp.noSlotInstructor || []);
        setRequestMinDate(resp.requestMinDate || '');
        setRequestMaxDate(resp.requestMaxDate || '');
        setIsCoaching(resp.isCoaching);
        setIsLesson(resp.isLesson);
        setCalendar(resp.calendar);
        setChkCal(resp.chkCal);
        setTrialCount(resp.trialCount);
        setIsTicketLess(resp.isTicketLess);
        setLessTicketCount(resp.lessTicketCount);
        // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
        const newLsnSvyAddTicketTerm = resp.LESSON_SURVEY_ADD_TICKET_TERM;
        const newLsnSvyAddTicketTermObj = typeof newLsnSvyAddTicketTerm === "string" ? JSON.parse(newLsnSvyAddTicketTerm) : newLsnSvyAddTicketTerm;
        if(newLsnSvyAddTicketTermObj?.START) {
          setLsnSvyAddTicketStart(new Date(newLsnSvyAddTicketTermObj.START));
        }
        if(newLsnSvyAddTicketTermObj?.END) {
          setLsnSvyAddTicketEnd(new Date(newLsnSvyAddTicketTermObj.END));
        }
        // 初期化
        setSelectedScheduleId(null);
      } catch(err) {
        if (err instanceof Error) {
          console.warn(err.message);
        }
      } finally {
        setIsFirstLoad(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 100)
      }
    })();

    // cleanup関数でキャンセルフラグを立てる
    return () => {
      cancelled = true;
    };
  }, [instId, lsnMin])


  /**
   * SPEAKOUT-1853 講師を変えたら「他の日時をリクエストする」の選択を解除する
   *
   * 予約の枠(selectedScheduleId)を API 取得のたびに初期化しているのと同じ理由。
   * 講師Aで「他の日時をリクエストする」を押したまま講師Bへ変えると、Bに空き枠が
   * たくさんあってもリクエストのままで、通常の予約へ戻る導線が
   * 「日程を選ぶ → ← 空き枠から選ぶ」しか無かった。
   * ★希望日時(requestPrefs)は【捨てない】。日時の希望は講師が誰かと関係が無く、
   *   講師を選び直しただけで入力し直しになるのは損なだけ（モーダルを閉じて開いても
   *   入力が残るのと同じ扱い）。リクエストに戻せばそのまま使える。
   * ★レッスン時間の変更では解除しない。同じ講師への希望なので、枠がある長さに
   *   変えたときは isLesson 側（isRequestMode）が自動で予約へ戻す。
   */
  useEffect(() => {
    setIsRequestSelected(false);
  }, [instId])


  // 無料回数
  useEffect(() => {
    if(user?.register_items?.live_lesson) {
      setTrialCount(user?.register_items?.live_lesson);
    } else {
      setTrialCount(0);
    }
  }, [user])


  // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
  useEffect(() => {
    let newIsLsnSvyAddTicketTerm = false;
    if(lsnSvyAddTicketStart && lsnSvyAddTicketEnd) {
      const now = new Date(); 
      newIsLsnSvyAddTicketTerm = Boolean(
        now >= lsnSvyAddTicketStart &&
        now < lsnSvyAddTicketEnd
      );
    }
    setIsLsnSvyAddTicketTerm(newIsLsnSvyAddTicketTerm);
  }, [lsnSvyAddTicketStart, lsnSvyAddTicketEnd])

  /**
   * SPEAKOUT-1853 希望日時をリクエストするモードか
   *
   * ★判定は「選択中の講師 × 選択中のレッスン時間で予約できる枠があるか」。
   *   サーバーは その条件で絞った枠だけを calendar に入れて返し、1つも無ければ isLesson=false
   *   になる（app/backend/api/functions/user/lesson/live.php）＝この1つで判定できる。
   *   枠が無ければ自動でリクエスト、枠があっても生徒が選べばリクエスト。
   */
  const isRequestMode = (isFirstLoad && !isLesson) || isRequestSelected;

  const contextVal = {
    lessonTermMinute: lessonTermMinute,
    // SPEAKOUT-1853 講師リクエスト（枠の無い講師 / 受付できる最も早い日 / 希望日時 / モード）
    noSlotInstructors: noSlotInstructors,
    requestMinDate: requestMinDate,
    requestMaxDate: requestMaxDate,
    requestPrefs: requestPrefs,
    setRequestPrefs: setRequestPrefs,
    isRequestMode: isRequestMode,
    isRequestSelected: isRequestSelected,
    setIsRequestSelected: setIsRequestSelected,
    detailInstId: detailInstId,
    setDetailInstId: setDetailInstId,
    user: user,
    config: config,
    vars: vars,
    setVars: setVars,
    title: title,
    coachingName: coachingName,
    isLiveMaintenance: isLiveMaintenance,
    isLesson: isLesson,
    isLoading: isLoading,
    formRef: formRef,
    // 講師データ
    instId: instId,
    setInstId: setInstId,
    instructors: instructors,
    // MT講師データ
    isCoaching: isCoaching,
    calendar: calendar,
    chkCal: chkCal,
    // 時間選択データ
    trialCount: trialCount,
    lsnMin: lsnMin,
    setLsnMin: setLsnMin,
    isTrial: isTrial,
    setIsTrial: setIsTrial,
    // 日時選択データ
    selectedScheduleId: selectedScheduleId,
    setSelectedScheduleId: setSelectedScheduleId,
    // シチュエーション
    displaySituations: displaySituations,
    selectedSituation: selectedSituation,
    setSelectedSituation: setSelectedSituation,
    // コメント
    requestContent: requestContent,
    setRequestContent: setRequestContent,
    // 送信
    isTicketLess: isTicketLess,
    lessTicketCount: lessTicketCount,
    // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
    isLsnSvyAddTicketTerm: isLsnSvyAddTicketTerm,
    lsnSvyAddTicketStart: lsnSvyAddTicketStart,
    lsnSvyAddTicketEnd: lsnSvyAddTicketEnd,
  }


  return (
    <LiveReserveContext.Provider value={contextVal}>
      {isFirstLoad ?
        <div style={{margin: '40px 0'}}>
          <h3 id="live_lesson_reservation" className="midashi1 bold">{title}を予約する</h3>

          {isLiveMaintenance ?
            <LiveReserveMaintenance />
          :
            <LiveReserveForm />
          }

          {/* 講師の詳細（プロフィール／レビュー）。
              ★講師選択モーダルより後に描画する＝重ねたときに上へ出す（どちらも z-index:1040） */}
          <LiveReserveInstructorDetail />

        </div>
      :
        <Loading />
      }
    </LiveReserveContext.Provider>
  );
}
  
export default LiveReserve;
  